<template>
  <div class="enlaces-page">
    <PageHeader title="Enlaces de acceso" subtitle="Comparte salidas públicamente con un enlace">
      <template #actions>
        <Button icon="pi pi-plus" label="Nuevo enlace" @click="showCreate = true" />
      </template>
    </PageHeader>

    <div class="enlaces-content">
      <div v-if="loading" class="text-center py-5 text-500">Cargando…</div>

      <EmptyState
        v-else-if="!enrichedTokens.length"
        icon="pi-link"
        title="Sin enlaces"
        description="Crea uno para compartir salidas públicamente."
      >
        <template #action>
          <Button icon="pi pi-plus" label="Nuevo enlace" @click="showCreate = true" />
        </template>
      </EmptyState>

      <div v-else class="flex flex-column gap-3">
        <div v-for="t in enrichedTokens" :key="t.token" class="enlace-card">
        <div class="flex align-items-start gap-3">
          <!-- Info -->
          <div class="flex-1 min-w-0">
            <div class="text-lg font-semibold mb-2">{{ t.name }}</div>

            <!-- Fechas -->
            <div class="flex align-items-center gap-2 text-sm mb-2">
              <i class="pi pi-calendar text-500" />
              <span v-if="t.dateRange" class="text-700">{{ t.dateRange }}</span>
              <span v-else class="text-400">Sin salidas asociadas</span>
            </div>

            <!-- Grupos -->
            <div v-if="t.gruposNames.length" class="flex flex-wrap gap-1 mb-2">
              <Tag v-for="g in t.gruposNames" :key="g" :value="g" severity="secondary" />
            </div>

            <!-- Salidas count -->
            <div class="text-xs text-500 mb-2">
              {{ t.salidaIds.length }} {{ t.salidaIds.length === 1 ? 'salida' : 'salidas' }} incluidas
            </div>

            <!-- Vista por defecto del enlace -->
            <div class="flex align-items-center gap-2 mb-2">
              <span class="text-xs text-500">Normal</span>
              <InputSwitch
                :modelValue="t.defaultView === 'mayores'"
                v-tooltip="'Vista por defecto del enlace'"
                @update:modelValue="cambiarVistaDefecto(t, $event ? 'mayores' : 'normal')"
              />
              <span class="text-xs text-500">Fácil (mayores)</span>
            </div>

            <!-- Link -->
            <div class="flex align-items-center gap-1 mt-2">
              <span
                class="text-xs text-400 font-mono overflow-hidden text-overflow-ellipsis white-space-nowrap flex-1"
                style="max-width:340px"
              >
                {{ publicUrl(t) }}
              </span>
              <Button
                :icon="copied === t.token ? 'pi pi-check' : 'pi pi-copy'"
                size="small"
                text
                :severity="copied === t.token ? 'success' : 'secondary'"
                v-tooltip="'Copiar enlace'"
                @click="copyLink(t)"
              />
            </div>
          </div>

          <!-- Acciones -->
          <div class="flex flex-column gap-2 flex-shrink-0">
            <Button
              icon="pi pi-map"
              label="Ver mapa"
              size="small"
              severity="success"
              outlined
              @click="openMonitor(t)"
            />
            <Button
              icon="pi pi-wifi"
              label="Ubicaciones"
              size="small"
              severity="info"
              outlined
              @click="openGeoModal(t)"
            />
            <Button
              icon="pi pi-share-alt"
              label="Metadata"
              size="small"
              severity="secondary"
              outlined
              v-tooltip="'Cómo se ve al compartirlo'"
              @click="openMetadata(t)"
            />
            <Button
              icon="pi pi-trash"
              size="small"
              severity="danger"
              text
              v-tooltip="'Eliminar enlace'"
              @click="confirmDelete(t)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog: crear enlace -->
    <Dialog v-model:visible="showCreate" header="Nuevo enlace" modal style="width:380px">
      <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
          <label class="text-sm">Nombre del enlace</label>
          <InputText
            v-model="newName"
            placeholder="Ej: Grupo Norte, Salida del sábado..."
            autofocus
            @keyup.enter="doCreate"
          />
        </div>
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" @click="showCreate = false" />
          <Button label="Crear" :disabled="!newName.trim()" :loading="creating" @click="doCreate" />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: geolocalizaciones en tiempo real -->
    <Dialog
      v-model:visible="showGeoModal"
      :header="`Ubicaciones en vivo — ${geoToken?.name ?? ''}`"
      modal
      style="width:680px;max-width:96vw"
      @show="initGeoMap"
      @hide="destroyGeoMap"
    >
      <div class="flex flex-column gap-3">
        <!-- Selector de día -->
        <div v-if="geoLinkedSalidas.length > 1" class="flex gap-2 overflow-x-auto">
          <Button
            v-for="(s, idx) in geoLinkedSalidas"
            :key="idx"
            :label="`${getDayName(s.date)} ${getLocalDate(s.date)}`"
            size="small"
            :outlined="geoActiveSalidaIdx !== idx"
            @click="geoActiveSalidaIdx = idx"
          />
        </div>

        <!-- Mapa -->
        <div class="border-round overflow-hidden border-1 surface-border" style="height:300px;position:relative">
          <div id="geo-modal-map" style="width:100%;height:100%" />
          <div
            v-if="!geoUsers.length"
            style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,.7);pointer-events:none"
          >
            <span class="text-500 text-sm">Sin usuarios compartiendo posición</span>
          </div>
        </div>

        <!-- Lista de usuarios -->
        <div v-if="!geoUsers.length" class="text-center text-500 text-sm py-2">
          Nadie está compartiendo su ubicación en este momento. El mapa se actualiza automáticamente.
        </div>
        <div v-else class="flex flex-column gap-2">
          <div
            v-for="u in geoUsers"
            :key="u.userName"
            class="flex align-items-center gap-3 p-2 border-1 surface-border border-round cursor-pointer hover:surface-hover"
            @click="centerGeoOn(u)"
          >
            <div
              class="w-2rem h-2rem border-round-xl flex align-items-center justify-content-center font-bold text-white text-sm flex-shrink-0"
              :style="`background:${colorFor(u.userName)}`"
            >
              {{ u.userName.charAt(0).toUpperCase() }}
            </div>
            <div class="flex-1">
              <div class="font-medium text-sm">{{ u.userName }}</div>
              <div class="text-xs text-500">Actualizado {{ formatAgo(u.ts) }}</div>
            </div>
            <a
              :href="`https://www.google.com/maps?q=${u.lat},${u.lng}`"
              target="_blank"
              class="text-primary text-sm"
              title="Abrir en Google Maps"
              @click.stop
            >
              <i class="pi pi-external-link" />
            </a>
          </div>
        </div>
      </div>
    </Dialog>

    <ConfirmDialog />
    </div><!-- /enlaces-content -->
  </div><!-- /enlaces-page -->
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouterStore } from '@/store/router'
import { useTerritoriosStore } from '@/store/territorios'
import { useSalidaTokens } from '@/composables/useSalidaTokens'
import { useApi } from '@/composables/useApi'
import { useConfirm } from 'primevue/useconfirm'
import Button from 'primevue/button'
import PageHeader from '@/components/ui/PageHeader.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import InputSwitch from 'primevue/inputswitch'
import Tag from 'primevue/tag'
import ConfirmDialog from 'primevue/confirmdialog'
import L from 'leaflet'
import { useMapTiles } from '@/composables/useMapTiles'
import { DEFAULT_CENTER } from '@/store/mapConstants'

const route = useRouterStore()
const territorios = useTerritoriosStore()
const api = useApi()
const confirm = useConfirm()
const { getPublicUrl, getTokens, createToken, revokeToken, setTokenDefaultView, getWsUrl } = useSalidaTokens()

// ── Estado principal ─────────────────────────────────────────────────────────
const loading = ref(true)
const tokens = ref([])
const salidas = ref([])

const publicUrl = (t) => getPublicUrl(t.token, t.defaultView)

const cambiarVistaDefecto = async (t, vista) => {
  if (t.defaultView === vista) return
  const original = tokens.value.find((x) => x.token === t.token)
  if (!original) return
  const prev = original.defaultView
  original.defaultView = vista
  const res = await setTokenDefaultView(t.token, vista)
  if (res.error) original.defaultView = prev
}

const enrichedTokens = computed(() => {
  return tokens.value.map((t) => {
    const linkedSalidas = salidas.value.filter((s) =>
      t.salidaIds.includes(String(s.id)) || t.salidaIds.includes(s.id)
    )

    // Rango de fechas
    let dateRange = null
    if (linkedSalidas.length) {
      const dates = linkedSalidas.map((s) => new Date(s.date)).filter(Boolean).sort((a, b) => a - b)
      const fmt = (d) => d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      dateRange = dates.length === 1
        ? fmt(dates[0])
        : `${fmt(dates[0])} → ${fmt(dates[dates.length - 1])}`
    }

    // Grupos únicos de todas las salidas
    const grupoIds = [...new Set(linkedSalidas.flatMap((s) => s.grupos || []))]
    const gruposNames = grupoIds
      .map((id) => {
        const g = territorios.grupos.find((g) => g.id === id)
        return g ? g.name : id
      })
      .filter(Boolean)

    return { ...t, dateRange, gruposNames }
  })
})

// ── Carga inicial ─────────────────────────────────────────────────────────────
const loadAll = async () => {
  loading.value = true
  const [tokensRes, salidasRes] = await Promise.all([
    getTokens(),
    api.get('/api/salidas'),
  ])
  tokens.value = tokensRes.tokens || []
  salidas.value = [...(salidasRes.salidas || []), ...(salidasRes.history || [])]
  loading.value = false
}

onMounted(loadAll)

// ── Crear enlace ──────────────────────────────────────────────────────────────
const showCreate = ref(false)
const newName = ref('')
const creating = ref(false)

const doCreate = async () => {
  if (!newName.value.trim()) return
  creating.value = true
  const res = await createToken(newName.value.trim())
  if (res.token) tokens.value.unshift(res)
  newName.value = ''
  showCreate.value = false
  creating.value = false
}

// ── Eliminar enlace ───────────────────────────────────────────────────────────
const confirmDelete = (t) => {
  confirm.require({
    message: `¿Eliminar el enlace "${t.name}"? Los usuarios que tengan el link ya no podrán acceder.`,
    header: 'Confirmar eliminación',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Eliminar',
    rejectLabel: 'Cancelar',
    accept: async () => {
      await revokeToken(t.token)
      tokens.value = tokens.value.filter((x) => x.token !== t.token)
    },
  })
}

// ── Copiar link ───────────────────────────────────────────────────────────────
const copied = ref(null)

const copyLink = (t) => {
  navigator.clipboard.writeText(getPublicUrl(t.token, t.defaultView))
  copied.value = t.token
  setTimeout(() => { if (copied.value === t.token) copied.value = null }, 2000)
}

// ── Editar la metadata de este enlace ─────────────────────────────────────────
const openMetadata = (t) => {
  route.page = 'metadataEnlaces'
  route.params = { type: 'salida', token: t.token }
}

// ── Ir al monitor ─────────────────────────────────────────────────────────────
const openMonitor = (t) => {
  route.page = 'monitorSalida'
  route.params = { token: t.token, name: t.name }
}

// ── Modal geolocalizaciones ───────────────────────────────────────────────────
const showGeoModal = ref(false)
const geoToken = ref(null)
const geoUsers = ref([])
const geoActiveSalidaIdx = ref(0)
let geoMap = null
const { addStreetTiles: _addGeoTiles } = useMapTiles(() => geoMap)
let geoWs = null
let geoPeMarker = null
const geoMarkers = {}

const geoLinkedSalidas = computed(() => {
  if (!geoToken.value) return []
  return salidas.value
    .filter((s) =>
      geoToken.value.salidaIds.includes(String(s.id)) ||
      geoToken.value.salidaIds.includes(s.id)
    )
    .sort((a, b) => (a.date || 0) - (b.date || 0))
})

const toLatlng = (v) => {
  if (!v) return null
  if (Array.isArray(v)) return [v[0], v[1]]
  return [Number(v.lat), Number(v.lng)]
}

const getDayName = (d) => {
  if (!d) return ''
  const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
  return days[new Date(d).getDay()]
}

const getLocalDate = (d) => {
  if (!d) return ''
  return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })
}

const updateGeoPeMarker = () => {
  geoPeMarker?.remove()
  geoPeMarker = null
  if (!geoMap) return
  const salida = geoLinkedSalidas.value[geoActiveSalidaIdx.value]
  const pe = salida?.puntoEncuentro?.latlng
  if (!pe) return
  const ll = toLatlng(pe)
  if (!ll) return
  const icon = L.divIcon({
    className: '',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    html: `<div style="width:26px;height:26px;background:#e07b39;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.3)"></div>`,
  })
  geoPeMarker = L.marker(ll, { icon })
    .addTo(geoMap)
    .bindPopup(`<b>Punto de encuentro</b><br>${salida.puntoEncuentro.name || ''}`)
  if (!geoUsers.value.length) {
    geoMap.setView(ll, 15)
  }
}

watch(geoActiveSalidaIdx, updateGeoPeMarker)

const COLORS = ['#007aff', '#e07b39', '#2e8b57', '#9b59b6', '#c0392b', '#16a085', '#d35400', '#1a6b7a']
const colorFor = (name) => {
  const sum = [...name].reduce((a, c) => a + c.charCodeAt(0), 0)
  return COLORS[Math.abs(sum) % COLORS.length]
}

const formatAgo = (ts) => {
  const s = Math.floor((Date.now() - ts) / 1000)
  if (s < 60) return `hace ${s}s`
  return `hace ${Math.floor(s / 60)}min`
}

const openGeoModal = (t) => {
  geoToken.value = t
  geoUsers.value = []
  const linked = salidas.value
    .filter((s) => t.salidaIds.includes(String(s.id)) || t.salidaIds.includes(s.id))
    .sort((a, b) => (a.date || 0) - (b.date || 0))
  const now = Date.now()
  let best = 0, bestDiff = Infinity
  linked.forEach((s, i) => {
    const diff = Math.abs((s.date || 0) - now)
    if (diff < bestDiff) { bestDiff = diff; best = i }
  })
  geoActiveSalidaIdx.value = best
  showGeoModal.value = true
}

const upsertGeoUser = (loc) => {
  const ts = loc.ts || Date.now()
  const ll = [loc.lat, loc.lng]
  const idx = geoUsers.value.findIndex((u) => u.userName === loc.userName)
  if (idx >= 0) {
    geoUsers.value[idx].lat = loc.lat
    geoUsers.value[idx].lng = loc.lng
    geoUsers.value[idx].ts = ts
  } else {
    geoUsers.value.push({ userName: loc.userName, lat: loc.lat, lng: loc.lng, ts })
  }

  if (!geoMap) return
  if (geoMarkers[loc.userName]) {
    geoMarkers[loc.userName].setLatLng(ll)
  } else {
    const color = colorFor(loc.userName)
    const initial = (loc.userName || '?').charAt(0).toUpperCase()
    const icon = L.divIcon({
      className: '',
      iconSize: [34, 34],
      iconAnchor: [17, 17],
      html: `<div style="width:34px;height:34px;border-radius:50%;background:${color};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:14px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">${initial}</div>`,
    })
    geoMarkers[loc.userName] = L.marker(ll, { icon })
      .addTo(geoMap)
      .bindPopup(`<b>${loc.userName}</b>`)
  }
}

const removeGeoUser = (userName) => {
  geoMarkers[userName]?.remove()
  delete geoMarkers[userName]
  geoUsers.value = geoUsers.value.filter((u) => u.userName !== userName)
}

const fitGeoMap = () => {
  if (!geoMap || !geoUsers.value.length) return
  const lls = geoUsers.value.map((u) => [u.lat, u.lng])
  if (lls.length === 1) geoMap.setView(lls[0], 16)
  else geoMap.fitBounds(L.latLngBounds(lls), { padding: [40, 40] })
}

const centerGeoOn = (u) => {
  if (!geoMap) return
  geoMap.setView([u.lat, u.lng], Math.max(geoMap.getZoom(), 16))
}

const initGeoMap = () => {
  const salida = geoLinkedSalidas.value[geoActiveSalidaIdx.value]
  const pe = salida?.puntoEncuentro?.latlng
  const defaultPunto = territorios.puntoEncuentro.find((p) => p.isDefault)?.latlng
  const fallbackCenter = defaultPunto ? toLatlng(defaultPunto) : DEFAULT_CENTER
  const initialCenter = pe ? (toLatlng(pe) || fallbackCenter) : fallbackCenter
  const initialZoom = pe ? 15 : 13
  geoMap = L.map('geo-modal-map').setView(initialCenter, initialZoom)
  _addGeoTiles(geoMap)

  updateGeoPeMarker()

  // WebSocket para actualizaciones en tiempo real
  geoWs = new WebSocket(getWsUrl(geoToken.value.token))
  geoWs.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data)
      if (msg.type === 'location') { upsertGeoUser(msg) }
      else if (msg.type === 'leave') { removeGeoUser(msg.userName) }
      else if (msg.type === 'snapshot') { msg.locations?.forEach(upsertGeoUser); fitGeoMap() }
    } catch {}
  }
  geoWs.onclose = () => {
    if (showGeoModal.value) setTimeout(initGeoWs, 3000)
  }
}

const initGeoWs = () => {
  if (!geoToken.value || !showGeoModal.value) return
  geoWs = new WebSocket(getWsUrl(geoToken.value.token))
  geoWs.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data)
      if (msg.type === 'location') { upsertGeoUser(msg) }
      else if (msg.type === 'leave') { removeGeoUser(msg.userName) }
      else if (msg.type === 'snapshot') { msg.locations?.forEach(upsertGeoUser); fitGeoMap() }
    } catch {}
  }
  geoWs.onclose = () => {
    if (showGeoModal.value) setTimeout(initGeoWs, 3000)
  }
}

const destroyGeoMap = () => {
  geoWs?.close()
  geoWs = null
  Object.values(geoMarkers).forEach((m) => m.remove())
  Object.keys(geoMarkers).forEach((k) => delete geoMarkers[k])
  geoPeMarker?.remove()
  geoPeMarker = null
  geoMap?.remove()
  geoMap = null
  geoUsers.value = []
  geoToken.value = null
}
</script>

<style scoped>
.enlaces-page {
  width: 100%;
  text-align: left;
}
.enlaces-content {
  padding: 0 var(--space-5) var(--space-5);
}
.enlace-card {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--space-4) var(--space-5);
  box-shadow: var(--shadow-1);
}
</style>
